import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { expensesApi, projectsApi, vendorsApi, unwrap, unwrapList } from "../api/resources";
import { useToast } from "../context/ToastContext";
import { useDayLock } from "../hooks/useDayLock";
import { useAsyncAction } from "../hooks/useAsyncAction";
import { useBuilderQuery } from "../hooks/useBuilderQuery";
import { Button, Card, Empty, Field, Input, Loader, Modal, Pagination, Select } from "../components/ui";
import { ConfirmDialog, LockedNote, RecordActions } from "../components/actions";
import { ExpenseForm, emptyExpenseForm, expensePayload, expenseToForm } from "../components/forms";
import { KhataSummary } from "../components/KhataSummary";
import { PaymentFields, ProofLine } from "../components/PaymentFields";
import { COPY } from "../utils/copy";
import { ACTIVE_PAYMENT_TYPES, PAYMENT_TYPE, emptyProof, isCreditPayment, paymentLabel, withProof } from "../utils/finance";
import { formatDate, formatMoney } from "../utils/format";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

export default function Expenses() {
  const { isBuilder, query, ready } = useBuilderQuery();
  const toast = useToast();
  const { isClosed, isTodayClosed, reload: reloadDays } = useDayLock();
  const { busy, run } = useAsyncAction();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const liveSearch = useDebouncedValue(search);
  const [category, setCategory] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [paymentType, setPaymentType] = useState("");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyExpenseForm);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [paying, setPaying] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", note: "", ...emptyProof() });
  const [vendorKhata, setVendorKhata] = useState(null);
  const openNew = params.get("new") === "1";

  const load = async () => {
    if (!ready) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [expRes, projectRes, vendorRes] = await Promise.all([
        expensesApi.list({ ...query, search: liveSearch, category, vendorId, paymentType, page, limit: 12 }),
        isBuilder ? projectsApi.list({ limit: 100 }) : Promise.resolve({ data: { items: [] } }),
        vendorsApi.list({ ...query, limit: 100 }),
      ]);
      const expenseData = unwrapList(expRes);
      const vendorList = unwrapList(vendorRes).items;
      setItems(expenseData.items);
      setPagination(expenseData.pagination);
      setProjects(unwrapList(projectRes).items);
      setVendors(vendorList);
      if (vendorId) {
        const vendor = vendorList.find((item) => item.id === vendorId);
        const khata = expenseData.khata || vendor?.khata;
        setVendorKhata(
          khata
            ? { id: vendorId, name: vendor?.name || "Vendor", khata }
            : null
        );
      } else {
        setVendorKhata(null);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, category, vendorId, paymentType, liveSearch, query.builderId]);

  useEffect(() => {
    setPage(1);
  }, [liveSearch]);

  const modalOpen = openNew || Boolean(editing);

  useEffect(() => {
    if (!modalOpen || !form.vendorId) return undefined;
    let cancelled = false;
    vendorsApi
      .khata(form.vendorId, query)
      .then((res) => {
        const data = unwrap(res);
        if (cancelled || !data?.khata) return;
        setVendors((prev) =>
          prev.map((item) => (item.id === form.vendorId ? { ...item, khata: data.khata } : item))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, form.vendorId, query.builderId]);

  const closeModal = () => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("new");
      return next;
    });
    setEditing(null);
    setForm(emptyExpenseForm);
  };

  const save = async (event) => {
    event.preventDefault();
    const result = await run(async () => {
      if (editing) return expensesApi.update(editing.id, expensePayload(form, { editing: true }));
      return expensesApi.create(expensePayload(form));
    }, editing
      ? "Expense updated. Daily book updated too."
      : form.paymentType === PAYMENT_TYPE.CREDIT
        ? "Udhar saved on the vendor's khata. Today's cash stayed the same."
        : "Expense added to today's book");

    if (result) {
      const payload = unwrap(result);
      const fallbackId = form.vendorId;
      closeModal();
      reloadDays();
      await load();
      if (payload?.khata) {
        const id = payload.vendor?.id || fallbackId || vendorId;
        setVendors((prev) =>
          prev.map((item) => (item.id === id ? { ...item, khata: payload.khata } : item))
        );
        setVendorKhata({
          id,
          name: payload.vendor?.name || vendors.find((item) => item.id === id)?.name || "Vendor",
          khata: payload.khata,
        });
      }
    }
  };

  const settle = async (event) => {
    event.preventDefault();
    if (!paying) return;
    const body = withProof({ amount: Number(payForm.amount), note: payForm.note || "" }, payForm);
    const result = await run(() => expensesApi.settle(paying.id, body), "Udhar paid on this expense.");
    if (result) {
      setPaying(null);
      reloadDays();
      load();
    }
  };

  const remove = async () => {
    if (!pendingDelete) return;
    const fallbackId = pendingDelete.vendor?.id;
    const result = await run(
      () => expensesApi.remove(pendingDelete.id),
      "Expense deleted. Money went back to the book."
    );
    if (result) {
      const payload = unwrap(result);
      setPendingDelete(null);
      reloadDays();
      await load();
      if (payload?.khata) {
        const id = payload.vendor?.id || fallbackId || vendorId;
        setVendors((prev) =>
          prev.map((item) => (item.id === id ? { ...item, khata: payload.khata } : item))
        );
        setVendorKhata({
          id,
          name: payload.vendor?.name || pendingDelete.vendor?.name || vendors.find((item) => item.id === id)?.name || "Vendor",
          khata: payload.khata,
        });
      }
    }
  };

  return (
    <div className="stack">
      <header className="page-head">
        <div>
          <p className="eyebrow">Money that went out</p>
          <h1>Expenses</h1>
        </div>
        {isBuilder ? (
          <Button icon="plus" onClick={() => setParams({ new: "1" })}>
            Add expense
          </Button>
        ) : null}
      </header>

      <form className="filters" onSubmit={(event) => event.preventDefault()}>
        <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by title" />
        <Select
          value={category}
          onChange={(event) => {
            setCategory(event.target.value);
            setPage(1);
          }}
        >
          <option value="">All types</option>
          <option value="general">Office / general</option>
          <option value="project">Project expense</option>
        </Select>
        <Select
          value={vendorId}
          onChange={(event) => {
            setVendorId(event.target.value);
            setPage(1);
          }}
        >
          <option value="">All vendors</option>
          {vendors.map((vendor) => (
            <option key={vendor.id} value={vendor.id}>
              {vendor.name}
            </option>
          ))}
        </Select>
        <Select
          value={paymentType}
          onChange={(event) => {
            setPaymentType(event.target.value);
            setPage(1);
          }}
        >
          <option value="">All payments</option>
          {ACTIVE_PAYMENT_TYPES.map((type) => (
            <option key={type} value={type}>
              {paymentLabel(type)}
            </option>
          ))}
          <option value="bank">Bank</option>
        </Select>
      </form>

      {vendorKhata ? (
        <Card>
          <div className="card-head">
            <h2>{vendorKhata.name}</h2>
            {vendorKhata.id ? (
              <Link to={`/vendors/${vendorKhata.id}`}>Open khata</Link>
            ) : null}
          </div>
          <KhataSummary khata={vendorKhata.khata} />
        </Card>
      ) : null}

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <Empty title="No expenses yet" text="Write cement, salary or office costs here." />
      ) : (
        <div className="sale-grid">
          {items.map((item) => {
            const locked = isClosed(item.date);
            return (
              <article key={item.id} className="sale-card credit-card">
                <div className="sale-card-top">
                  <h3>{item.title}</h3>
                  {item.canSettle ? (
                    <span className="pill pill-credit">Due {formatMoney(item.remainingAmount)}</span>
                  ) : (
                    <span className={`pill ${isCreditPayment(item.paymentType) ? "pill-credit" : ""}`}>
                      {paymentLabel(item.paymentType)}
                    </span>
                  )}
                </div>
                <p>
                  {item.vendor?.name || "Vendor"} ·{" "}
                  {item.category === "project" ? item.project?.name || "Project" : "General"} · {formatDate(item.date)}
                  {locked ? " · Closed day" : ""}
                </p>
                <div className="sale-meta">
                  <div>
                    <span>Bill</span>
                    <b className={isCreditPayment(item.paymentType) ? "" : "minus"}>{formatMoney(item.amount)}</b>
                  </div>
                  {item.canSettle || item.paidAmount ? (
                    <>
                      <div>
                        <span>Paid</span>
                        <b>{formatMoney(item.paidAmount)}</b>
                      </div>
                      <div>
                        <span>Due</span>
                        <b>{formatMoney(item.remainingAmount)}</b>
                      </div>
                    </>
                  ) : null}
                </div>
                <ProofLine
                  paymentType={item.paymentType}
                  referenceNumber={item.referenceNumber}
                  image={item.image}
                  bank={item.bank}
                />
                <RecordActions
                  disableEdit={locked}
                  disableDelete={locked}
                  onEdit={() => {
                    setEditing(item);
                    setForm(expenseToForm(item));
                  }}
                  onDelete={() => setPendingDelete(item)}
                >
                  <Link className="btn btn-ghost btn-sm" to={`/expenses/${item.id}/statement`}>
                    Statement
                  </Link>
                  {isBuilder && item.canSettle ? (
                    <Button
                      className="btn-sm"
                      disabled={isTodayClosed}
                      title={isTodayClosed ? COPY.dayClosedHint : undefined}
                      onClick={() => {
                        setPaying(item);
                        setPayForm({ amount: String(item.remainingAmount || ""), note: "", ...emptyProof() });
                      }}
                    >
                      Pay
                    </Button>
                  ) : null}
                </RecordActions>
              </article>
            );
          })}
        </div>
      )}

      <Pagination page={pagination.page} totalPages={pagination.totalPages} onPage={setPage} />

      <Modal
        open={modalOpen}
        title={editing ? "Edit expense" : "New expense"}
        onClose={closeModal}
        footer={
          <>
            <Button variant="ghost" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="expense-form"
              disabled={busy || Boolean(editing && isClosed(editing.date)) || (!editing && vendors.length === 0)}
            >
              Save
            </Button>
          </>
        }
      >
        <LockedNote show={Boolean(editing && isClosed(editing.date))} />
        <ExpenseForm
          form={form}
          onChange={setForm}
          onSubmit={save}
          projects={projects}
          vendors={vendors}
          editing={Boolean(editing)}
        />
      </Modal>

      <Modal
        open={Boolean(paying)}
        title={paying ? `Pay ${paying.title}` : "Pay udhar"}
        onClose={() => setPaying(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPaying(null)}>
              Cancel
            </Button>
            <Button type="submit" form="expense-pay-form" disabled={busy}>
              Save payment
            </Button>
          </>
        }
      >
        {paying ? (
          <form id="expense-pay-form" className="form-grid" onSubmit={settle}>
            <Field label="Amount" hint={`Maximum ${formatMoney(paying.remainingAmount)}`}>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                max={paying.remainingAmount}
                required
                value={payForm.amount}
                onChange={(event) => setPayForm((prev) => ({ ...prev, amount: event.target.value }))}
              />
            </Field>
            <PaymentFields form={payForm} onChange={setPayForm} mode="settle" label="Pay with" />
            <Field label="Note" wide>
              <Input
                value={payForm.note}
                onChange={(event) => setPayForm((prev) => ({ ...prev, note: event.target.value }))}
                placeholder="Optional"
              />
            </Field>
          </form>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete expense?"
        text={COPY.deleteExpense}
        busy={busy}
        onClose={() => setPendingDelete(null)}
        onConfirm={remove}
      />
    </div>
  );
}
